<script setup lang="ts">
  import {
    useApiRequestsAbort,
    useGoogleAnalytics,
    usePerformanceTiming,
  } from '@/shared/composables'
  import { useCommonStore, useQuoteStore, useUserStore } from '@/shared/store'
  import { defineAsyncComponent, onBeforeMount, ref } from 'vue'
  import { OutboundFlowRoutes } from '@/shared/types/router.types'
  import {
    type Country,
    CountryBoundType,
    FileCategory,
    type Plan,
    PromoCodeTypes,
    WordingDocName,
  } from '@/shared/types/api.types'
  import { useRouter } from 'vue-router'
  import { LOAD_EVENT_NAME } from '@/widgets/Flow/FlowSelectPlanWidget/FlowSelectPlanWidget.constants'
  import { useI18n } from 'vue-i18n'
  import { TravelDirection } from '@/shared/types/common.types'
  import { AnalyticsPageName } from '@/shared/constants/analytics.constants'
  import { useFormDataWithRedirect } from '@/shared/composables/useFormDataWithRedirect'

  const emit = defineEmits<Emits>()

  const FlowSelectPlanWidgetV3 = defineAsyncComponent(() =>
    import('@/widgets/Flow/FlowSelectPlanWidget').then((m) => m.FlowSelectPlanWidgetV3)
  )

  interface Emits {
    (e: typeof LOAD_EVENT_NAME, request: () => Promise<any>): void
  }

  useApiRequestsAbort()
  const commonStore = useCommonStore()
  const userStore = useUserStore()
  const quoteStore = useQuoteStore()

  const { push } = useRouter()
  const { getFormData } = useFormDataWithRedirect()
  const { onPageReady, onPerformanceCheckEnd } = usePerformanceTiming()
  const gaService = useGoogleAnalytics()

  const { locale } = useI18n()

  const plans = ref<Plan[]>([])
  const policyWording = ref('')
  const listOfDestionations = ref<Country[]>([])

  const sendShowPlansAnalytics = () => {
    if (!plans.value || plans.value.length === 0) return

    const showPlansEventParams = plans.value.map((plan) => ({
      item_name: plan.name,
      price: plan.price,
      schemecode: plan.schemecode,
    }))

    gaService.sendOutboundSelectPlanShowPlans(showPlansEventParams)
  }

  const fetchQatarWordings = () =>
    commonStore
      .getQatarWordings(FileCategory.TRAVEL, WordingDocName.POLICY_WORDING)
      .then((data) => {
        policyWording.value = data[0]?.Link || ''
      })

  const fetchCountries = () =>
    userStore.fetchCountries(CountryBoundType.OUTBUND, locale.value).then((data) => {
      listOfDestionations.value = data
    })

  const getQuote = async (): Promise<void> => {
    const destinationIds = userStore.destination.map((country) => country?.id)
    if (!destinationIds.length) {
      await push({ name: OutboundFlowRoutes.DESTINATION })
      return
    }
    let type: PromoCodeTypes | undefined
    if (userStore.isAffinity && userStore.promocode) {
      type = PromoCodeTypes.AFFINITY
    }
    const quote = await quoteStore.getTravelQuoteUae(
      userStore.promocode ?? '',
      0,
      destinationIds,
      type
    )
    if (quote.schemes.length === 0) {
      return Promise.reject('quote.schemes.length === 0')
    } else {
      plans.value = quote.schemes
      quoteStore.setProductBonus(quote.productBonus, true)
      quoteStore.setCoinsDiscountPrice(quote.coinsDiscountPrice, true)
      quoteStore.setQuoteWithCoinsDiscount(quote.quoteWithCoinsDiscount, true)
      sendShowPlansAnalytics()
    }
  }

  onBeforeMount(async () => {
    emit(LOAD_EVENT_NAME, async () => {
      onPageReady()

      if (!(await getFormData(TravelDirection.OUTBOUND, false))) return
      await Promise.all([getQuote(), fetchQatarWordings(), fetchCountries()])

      onPerformanceCheckEnd()
      gaService.sendViewPage(TravelDirection.OUTBOUND, AnalyticsPageName.SELECT_PLAN)
    })
  })
</script>

<template>
  <FlowSelectPlanWidgetV3
    :plans="plans"
    :policy-wording="policyWording"
    :list-of-destionations="listOfDestionations"
  />
</template>
